/**
 * 身份与租户枚举 —— `users` / `ledger_members` 的取值
 *
 * 依据：规格 §16 · 架构 §4.2 · `DR-018` · `DR-020`（注销冷静期）
 *
 * 🔴 **形态约定（全 `src/enums/` 一致）**：用 `as const` 对象 + `typeof` 抽联合类型，
 * **不用 TS `enum`** —— 理由：① 零运行期开销（就是普通对象）② 与 Zod / JSON 序列化天然兼容
 * ③ 无 `isolatedModules` 下的 `const enum` 陷阱 ④ 可直接 `Object.values()` 遍历做校验与 CI 断言。
 */

// ─────────────────────────── 平台角色 ───────────────────────────

/**
 * 平台角色（`users.role`）—— 🔴 **与账本角色（`LedgerRole`）解耦**。
 *
 * 🔴 `admin` **无 Web 提权接口**（规格 §16 红线）：取得 admin 只能经 CLI（`DR-027`）。
 */
export const UserRole = {
  User: 'user',
  Admin: 'admin',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

// ─────────────────────────── 账号状态 ───────────────────────────

/**
 * 账号状态（`users.status`）。
 *
 * 🔴 **注销 7 天冷静期 = `Frozen`**（N 域）：冷静期内**不可登录写入**、**可随时撤销**、剩余天数可见。
 */
export const UserStatus = {
  Active: 'active',
  PendingDeletion: 'pending_deletion',
  Frozen: 'frozen',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

// ─────────────────────────── 账本角色 ───────────────────────────

/**
 * 账本角色（`ledger_members.role`）—— 🔴 **只收窄为 2 值**（规格 §16 / `DR-018`）。
 *
 * - `Owner` / `Member` 的**数据可见范围完全一致**，差别只在**协作管理动作**（见 `PermissionAction`）；
 * - 🔴 **成员上限 4（含 owner）**，硬校验在服务端；
 * - 🔴 角色取得方式：**邀请码恒发 `Member`**；`Owner` 只能通过**转让所有权**获得，
 *   **无「加入后改角色」接口**。
 */
export const LedgerRole = {
  Owner: 'owner',
  Member: 'member',
} as const;
export type LedgerRole = (typeof LedgerRole)[keyof typeof LedgerRole];
